import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SOCIAL_LINKS, TikTokIcon } from "./social";
import { LocalizedLink } from "../../lib/i18nRouting";
import { HOLIDAYS_PATH } from "../../data/holidays";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="bg-forest-deep text-bone relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-wider-3 opacity-70">Apuseni Active</div>
            <h3 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
              {t("footer.big_a")}<br/>{t("footer.big_b")}
            </h3>
            <p className="mt-6 text-bone/70 max-w-md text-sm leading-relaxed">
              {t("footer.about")}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-wider-3 opacity-70">{t("footer.nav_title")}</div>
            <ul className="mt-6 space-y-3 text-sm">
              <li><LocalizedLink to="/" className="link-underline">{t("nav.home")}</LocalizedLink></li>
              <li><LocalizedLink to="/experiente" className="link-underline">{t("nav.experiences")}</LocalizedLink></li>
              <li><LocalizedLink to="/apuseni" className="link-underline">{t("nav.apuseni")}</LocalizedLink></li>
              <li><LocalizedLink to={HOLIDAYS_PATH} className="link-underline" data-testid="footer-nav-holidays">{t("nav.holidays")}</LocalizedLink></li>
              <li><LocalizedLink to="/contact" className="link-underline">{t("nav.contact")}</LocalizedLink></li>
              <li><LocalizedLink to="/rezervare" className="link-underline">{t("footer.book_link")}</LocalizedLink></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-wider-3 opacity-70">{t("footer.contact_title")}</div>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 opacity-70" />
                <a href="tel:0752354222" data-testid="footer-phone" className="link-underline">0752 354 222</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 opacity-70" />
                <a href="mailto:contact@apuseniactive.ro" data-testid="footer-email" className="link-underline">contact@apuseniactive.ro</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 opacity-70" />
                <span>Oradea · Băile Felix · Băile 1 Mai</span>
              </li>
            </ul>
            <div className="flex gap-5 mt-8" data-testid="footer-socials">
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" data-testid="footer-social-instagram" className="opacity-70 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300" aria-label="Instagram"><Instagram className="w-5 h-5 stroke-[1.5]" /></a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" data-testid="footer-social-facebook" className="opacity-70 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300" aria-label="Facebook"><Facebook className="w-5 h-5 stroke-[1.5]" /></a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noreferrer" data-testid="footer-social-tiktok" className="opacity-70 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300" aria-label="TikTok"><TikTokIcon className="w-5 h-5" strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-10 border-t border-bone/15">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="text-[10px] uppercase tracking-wider-3 opacity-60">{t("footer.found_on")}</div>
            <a href="https://www.getyourguide.com/-s787216" target="_blank" rel="noreferrer" data-testid="footer-getyourguide" className="inline-block opacity-90 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300" aria-label="GetYourGuide | Apuseni Active">
              <img src="https://gyg.me/kUtga42u" width="160" height="60" loading="lazy" alt="GetYourGuide | Apuseni Active" style={{ border: "1px solid #c6c8d0" }} />
            </a>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs opacity-60">
            <div>{t("footer.copyright", { year: new Date().getFullYear() })}</div>
            <div className="flex gap-6">
              <Link to="/admin/login" className="link-underline" data-testid="footer-admin-link">{t("footer.admin")}</Link>
              <span>{t("footer.location")}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
